import { useCallback } from 'react';

import { publishBoardSocketPackage } from '@walls/services/boardWebsockets';
import { wallsApi } from '@walls/services/wallsApi';
import {
  useBoardDrawingStore,
  useBoardSelectionStore,
  useWallContentStore,
  useWallStore,
  type ShapeId,
  type WallBoard,
  type WallShape,
} from '@walls/store';
import { getShapeType } from '../../drawing/utils/shapeAttributes';
import { sortBoardZIndexes } from '../../positioning/zIndex';

const duplicateOffset = 10;

export function useShapeCommands(board: WallBoard) {
  const addShape = useWallContentStore((state) => state.addShape);
  const cards = useWallContentStore((state) => state.cards);
  const shapes = useWallContentStore((state) => state.shapes);
  const removeShape = useWallContentStore((state) => state.removeShape);
  const updateCardZIndexes = useWallContentStore((state) => state.updateCardZIndexes);
  const updateShape = useWallContentStore((state) => state.updateShape);
  const updateShapeZIndexes = useWallContentStore((state) => state.updateShapeZIndexes);
  const setSelectedShape = useBoardDrawingStore((state) => state.setSelectedShape);
  const setCurrentElement = useBoardSelectionStore((state) => state.setCurrentElement);
  const topicKey = useWallStore((state) => state.topicKey);

  return {
    createShape: useCallback(
      async (shape: WallShape) => {
        addShape(shape);
        setSelectedShape(shape.id);

        try {
          const savedShape = await wallsApi.insertShape(
            board.id,
            shape.type,
            shape.attributes,
            shape.zPos ?? 1,
            topicKey
          );

          removeShape(shape.id);
          addShape(savedShape);
          setSelectedShape(savedShape.id);
          publishBoardSocketPackage({
            action: 'addShape',
            topic: board.id,
            shape: savedShape,
          });

          return savedShape;
        } catch (error) {
          removeShape(shape.id);
          setSelectedShape(null);
          throw error;
        }
      },
      [addShape, board.id, removeShape, setSelectedShape, topicKey]
    ),

    duplicateShape: useCallback(
      async (shape: WallShape) => {
        const duplicatedShape = await wallsApi.insertShape(
          board.id,
          shape.type,
          getDuplicatedShapeAttributes(shape),
          shape.zPos ?? 1,
          topicKey
        );
        const nextShapes = [...shapes, duplicatedShape];
        const sortedZIndexes = sortBoardZIndexes(cards, nextShapes, duplicatedShape.id);
        const duplicatedShapeZPos =
          sortedZIndexes.shapes.find(({ shapeId }) => shapeId === duplicatedShape.id)?.zPos ??
          duplicatedShape.zPos;
        const placedDuplicatedShape = { ...duplicatedShape, zPos: duplicatedShapeZPos };

        addShape(placedDuplicatedShape);
        updateCardZIndexes(sortedZIndexes.cards);
        updateShapeZIndexes(sortedZIndexes.shapes);
        setSelectedShape(placedDuplicatedShape.id);
        setCurrentElement({
          id: placedDuplicatedShape.id,
          type: getShapeElementType(placedDuplicatedShape),
        });

        void Promise.all([
          sortedZIndexes.cards.length > 0
            ? wallsApi.updateCardZIndexes(board.id, sortedZIndexes.cards, topicKey)
            : Promise.resolve(),
          sortedZIndexes.shapes.length > 0
            ? wallsApi.updateShapeZIndexes(board.id, sortedZIndexes.shapes, topicKey)
            : Promise.resolve(),
        ]);
        publishBoardSocketPackage({
          action: 'addShape',
          topic: board.id,
          shape: placedDuplicatedShape,
        });

        return placedDuplicatedShape;
      },
      [
        addShape,
        board.id,
        cards,
        setCurrentElement,
        setSelectedShape,
        shapes,
        topicKey,
        updateCardZIndexes,
        updateShapeZIndexes,
      ]
    ),

    deleteShape: useCallback(
      async (shapeId: ShapeId) => {
        await wallsApi.deleteShape(board.id, shapeId, topicKey);
        removeShape(shapeId);
        setSelectedShape(null);
        setCurrentElement(null);
        publishBoardSocketPackage({
          action: 'deleteShape',
          topic: board.id,
          shapeId,
        });
      },
      [board.id, removeShape, setCurrentElement, setSelectedShape, topicKey]
    ),

    lockShape: useCallback(
      async (shapeId: ShapeId) => {
        updateShape(shapeId, { isLocked: true });
        setSelectedShape(null);
        setCurrentElement(null);

        try {
          await wallsApi.updateShapeLocked(board.id, shapeId, true, topicKey);
          publishBoardSocketPackage({
            action: 'setShapeLocked',
            topic: board.id,
            shapeId,
            isLocked: true,
          });
        } catch (error) {
          updateShape(shapeId, { isLocked: false });
          throw error;
        }
      },
      [board.id, setCurrentElement, setSelectedShape, topicKey, updateShape]
    ),

    unlockShape: useCallback(
      async (shapeId: ShapeId) => {
        updateShape(shapeId, { isLocked: false });
        setSelectedShape(null);
        setCurrentElement(null);

        try {
          await wallsApi.updateShapeLocked(board.id, shapeId, false, topicKey);
          publishBoardSocketPackage({
            action: 'setShapeLocked',
            topic: board.id,
            shapeId,
            isLocked: false,
          });
        } catch (error) {
          updateShape(shapeId, { isLocked: true });
          throw error;
        }
      },
      [board.id, setCurrentElement, setSelectedShape, topicKey, updateShape]
    ),

    persistShape: useCallback(
      async (shape: WallShape) => {
        const savedShape = await wallsApi.updateShape(
          board.id,
          shape.id,
          shape.type,
          shape.attributes,
          shape.zPos ?? 1,
          topicKey
        );

        updateShape(shape.id, savedShape);
        publishBoardSocketPackage({
          action: 'updateShape',
          topic: board.id,
          shape: savedShape,
        });

        return savedShape;
      },
      [board.id, topicKey, updateShape]
    ),

    updateShapeAttributes: useCallback(
      (shapeId: ShapeId, attributes: Record<string, unknown>) => {
        updateShape(shapeId, { attributes });
      },
      [updateShape]
    ),

    updateShapeAttributesAndPersist: useCallback(
      async (shape: WallShape, attributesPatch: Record<string, unknown>) => {
        const updatedShape = {
          ...shape,
          attributes: {
            ...shape.attributes,
            ...attributesPatch,
          },
        };

        updateShape(shape.id, { attributes: updatedShape.attributes });

        try {
          await wallsApi.updateShape(
            board.id,
            updatedShape.id,
            updatedShape.type,
            updatedShape.attributes,
            updatedShape.zPos ?? 1,
            topicKey
          );
          publishBoardSocketPackage({
            action: 'updateShape',
            topic: board.id,
            shape: updatedShape,
          });
        } catch (error) {
          updateShape(shape.id, { attributes: shape.attributes });
          throw error;
        }
      },
      [board.id, topicKey, updateShape]
    ),

    updateShapeZPos: useCallback(
      (shapeId: ShapeId, zPos: number) => {
        updateShape(shapeId, { zPos });
      },
      [updateShape]
    ),
  };
}

function getDuplicatedShapeAttributes(shape: WallShape) {
  const attributes = { ...shape.attributes };

  if (getShapeType(shape) === 'line') {
    return {
      ...attributes,
      startX: offsetCoordinate(attributes.startX),
      startY: offsetCoordinate(attributes.startY),
      endX: offsetCoordinate(attributes.endX),
      endY: offsetCoordinate(attributes.endY),
    };
  }

  return {
    ...attributes,
    x: offsetCoordinate(attributes.x),
    y: offsetCoordinate(attributes.y),
  };
}

function offsetCoordinate(value: unknown) {
  const coordinate = Number(value);

  return (Number.isFinite(coordinate) ? coordinate : 0) + duplicateOffset;
}

function getShapeElementType(shape: WallShape): 'shape' | 'line' | 'text' {
  const shapeType = getShapeType(shape);

  return shapeType === 'line' || shapeType === 'text' ? shapeType : 'shape';
}
